import { isEqual } from "lodash";
import moment from "moment";
import {
  ComplianceProgramPerformanceReportRowFragment,
  ComplianceProgramReportAssessmentFragment,
  ComplianceProgramTargetFragment,
  PerformanceAssessmentAnswerFilter,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import { CareerTrackPickerTrack } from "@apps/org-settings/components/competencies/career-track-picker";
import { CareerTrackRolePickerRole } from "@apps/org-settings/components/competencies/career-track-role-picker";

import { isAssessmentReportAnswerType } from "../../helpers";
import { TeamPickerTeam } from "../../team-picker";
import {
  AssessmentReportFilterItem,
  AssessmentReportFilterItemOptions,
  AssessmentReportFilterMatchType,
  AssessmentReportFilterType,
} from "./types";

export const isFilterEqual = (
  a: AssessmentReportFilterItem,
  b: AssessmentReportFilterItem
) => {
  return a.type === b.type && isEqual(a.options, b.options);
};

export const hasFilterType = (
  filters: AssessmentReportFilterItem[],
  type: AssessmentReportFilterType,
  options: AssessmentReportFilterItemOptions = {}
) => {
  return filters.some((f) => {
    return isFilterEqual(f, { type, data: [], options });
  });
};

export function getFilterByType(
  filters: AssessmentReportFilterItem[],
  type: AssessmentReportFilterType.ENROLLMENT_DATE
): (string | null)[];
export function getFilterByType(
  filters: AssessmentReportFilterItem[],
  type: AssessmentReportFilterType.TEAM
): TeamPickerTeam[];
export function getFilterByType(
  filters: AssessmentReportFilterItem[],
  type: AssessmentReportFilterType.ROLE
): CareerTrackRolePickerRole[];
export function getFilterByType(
  filters: AssessmentReportFilterItem[],
  type: AssessmentReportFilterType.CAREER_TRACK
): CareerTrackPickerTrack[];
export function getFilterByType(
  filters: AssessmentReportFilterItem[],
  type: AssessmentReportFilterType.CAREER_TRACK_LEVEL
): number[];
export function getFilterByType(
  filters: AssessmentReportFilterItem[],
  type: AssessmentReportFilterType.USER
): BasicUser[];
export function getFilterByType(
  filters: AssessmentReportFilterItem[],
  type: AssessmentReportFilterType.REPORTS_TO
): BasicUser[];
export function getFilterByType(
  filters: AssessmentReportFilterItem[],
  type: AssessmentReportFilterType.ANSWER
): PerformanceAssessmentAnswerFilter[];
export function getFilterByType(
  filters: AssessmentReportFilterItem[],
  type: AssessmentReportFilterType.TALENT_INDICATOR
): PerformanceAssessmentAnswerFilter[];
export function getFilterByType(
  filters: AssessmentReportFilterItem[],
  type: AssessmentReportFilterType
) {
  const filtersOfType = filters.filter((f) => f.type === type);
  if (filtersOfType.length === 0) {
    return [];
  }
  return filtersOfType.reduce(
    (acc, filter) => acc.concat(filter.data),
    [] as unknown[]
  );
}

export function updateFilters(
  existingFilters: AssessmentReportFilterItem[],
  type: AssessmentReportFilterType.ENROLLMENT_DATE,
  data: (string | null)[],
  options?: AssessmentReportFilterItemOptions
): AssessmentReportFilterItem[];
export function updateFilters(
  existingFilters: AssessmentReportFilterItem[],
  type: AssessmentReportFilterType.TEAM,
  data: TeamPickerTeam[],
  options?: AssessmentReportFilterItemOptions
): AssessmentReportFilterItem[];
export function updateFilters(
  existingFilters: AssessmentReportFilterItem[],
  type: AssessmentReportFilterType.ROLE,
  data: CareerTrackRolePickerRole[],
  options?: AssessmentReportFilterItemOptions
): AssessmentReportFilterItem[];
export function updateFilters(
  existingFilters: AssessmentReportFilterItem[],
  type: AssessmentReportFilterType.CAREER_TRACK,
  data: CareerTrackPickerTrack[],
  options?: AssessmentReportFilterItemOptions
): AssessmentReportFilterItem[];
export function updateFilters(
  existingFilters: AssessmentReportFilterItem[],
  type: AssessmentReportFilterType.CAREER_TRACK_LEVEL,
  data: number[],
  options?: AssessmentReportFilterItemOptions
): AssessmentReportFilterItem[];
export function updateFilters(
  existingFilters: AssessmentReportFilterItem[],
  type: AssessmentReportFilterType.USER,
  data: BasicUser[],
  options?: AssessmentReportFilterItemOptions
): AssessmentReportFilterItem[];
export function updateFilters(
  existingFilters: AssessmentReportFilterItem[],
  type: AssessmentReportFilterType.REPORTS_TO,
  data: BasicUser[],
  options?: AssessmentReportFilterItemOptions
): AssessmentReportFilterItem[];
export function updateFilters(
  existingFilters: AssessmentReportFilterItem[],
  type: AssessmentReportFilterType.ANSWER,
  data: PerformanceAssessmentAnswerFilter[],
  options?: AssessmentReportFilterItemOptions
): AssessmentReportFilterItem[];
export function updateFilters(
  existingFilters: AssessmentReportFilterItem[],
  type: AssessmentReportFilterType.TALENT_INDICATOR,
  data: PerformanceAssessmentAnswerFilter[],
  options?: AssessmentReportFilterItemOptions
): AssessmentReportFilterItem[];
export function updateFilters(
  existingFilters: AssessmentReportFilterItem[],
  type: AssessmentReportFilterType,
  data:
    | number[]
    | (string | null)[]
    | BasicUser[]
    | CareerTrackRolePickerRole[]
    | CareerTrackPickerTrack[]
    | PerformanceAssessmentAnswerFilter[],
  options?: AssessmentReportFilterItemOptions
): AssessmentReportFilterItem[];
export function updateFilters(
  existingFilters: AssessmentReportFilterItem[],
  type: AssessmentReportFilterType,
  data:
    | number[]
    | (string | null)[]
    | BasicUser[]
    | CareerTrackRolePickerRole[]
    | CareerTrackPickerTrack[]
    | PerformanceAssessmentAnswerFilter[],
  options: AssessmentReportFilterItemOptions = {}
) {
  if (hasFilterType(existingFilters, type, options)) {
    return existingFilters.map((filter) => {
      if (
        isFilterEqual(filter, {
          type,
          data,
          options,
        } as AssessmentReportFilterItem)
      ) {
        return {
          type,
          data,
          options,
        };
      }
      return filter;
    });
  }

  return [...existingFilters, { type, data, options }];
}

export enum FilterResult {
  NOT_APPLICABLE = "NOT_APPLICABLE", // Filter is empty/disabled
  MATCH = "MATCH", // Filter criteria met
  NO_MATCH = "NO_MATCH", // Filter criteria not met
}

const matchesUserFilter = (
  userFilterList: BasicUser[],
  target: ComplianceProgramTargetFragment
) => {
  if (userFilterList.length === 0) {
    return FilterResult.NOT_APPLICABLE;
  }
  return !!userFilterList.find((u) => u.id === target.id)
    ? FilterResult.MATCH
    : FilterResult.NO_MATCH;
};

const matchesReportsToFilter = (
  reportsToFilterList: BasicUser[],
  target: ComplianceProgramTargetFragment
) => {
  if (reportsToFilterList.length === 0) {
    return FilterResult.NOT_APPLICABLE;
  }
  return !!reportsToFilterList.find((u) => {
    return target.primaryManager?.id === u.id;
  })
    ? FilterResult.MATCH
    : FilterResult.NO_MATCH;
};

const matchesTeamsFilter = (
  teamsFilterList: TeamPickerTeam[],
  target: ComplianceProgramTargetFragment
) => {
  if (teamsFilterList.length === 0) {
    return FilterResult.NOT_APPLICABLE;
  }
  return !!teamsFilterList.find((t) => {
    const teams = target.teamsList ? target.teamsList : [];
    return teams.map((team) => team.id).includes(t.id);
  })
    ? FilterResult.MATCH
    : FilterResult.NO_MATCH;
};

const matchesEnrollmentDateFilter = (
  dateList: (string | null)[],
  targetAddedToProgramOn?: string | null
) => {
  const startDate = dateList[0];
  const endDate = dateList[1];
  if (!startDate && !endDate) {
    return FilterResult.NOT_APPLICABLE;
  } else if (!endDate) {
    return moment(targetAddedToProgramOn).isAfter(startDate)
      ? FilterResult.MATCH
      : FilterResult.NO_MATCH;
  } else if (!startDate) {
    return moment(targetAddedToProgramOn).isBefore(moment(endDate).endOf("day"))
      ? FilterResult.MATCH
      : FilterResult.NO_MATCH;
  }
  return targetAddedToProgramOn &&
    moment(targetAddedToProgramOn).isBetween(startDate, endDate, "day", "[]")
    ? FilterResult.MATCH
    : FilterResult.NO_MATCH;
};

const matchesRoleFilter = (
  roleFilterList: CareerTrackRolePickerRole[],
  roles: ReadonlyArray<{ id: number }>
) => {
  if (roleFilterList.length === 0) {
    return FilterResult.NOT_APPLICABLE;
  }
  const currentRoleIds = roles.map((role) => role.id);
  return roleFilterList.some((r) => currentRoleIds.includes(r.id))
    ? FilterResult.MATCH
    : FilterResult.NO_MATCH;
};

const matchesCareerTrackFilter = (
  careerTrackFilterList: CareerTrackPickerTrack[],
  roles: ReadonlyArray<{ careerTrack: { id: number } }>
) => {
  if (careerTrackFilterList.length === 0) {
    return FilterResult.NOT_APPLICABLE;
  }
  const currentCareerTrackIds = roles.map((role) => role.careerTrack.id);
  return careerTrackFilterList.some((r) => currentCareerTrackIds.includes(r.id))
    ? FilterResult.MATCH
    : FilterResult.NO_MATCH;
};

const matchesCareerTrackLevelFilter = (
  levelFilterList: number[],
  roles: ReadonlyArray<{ level: number }>
) => {
  if (levelFilterList.length === 0) {
    return FilterResult.NOT_APPLICABLE;
  }
  const currentRoleLevels = roles.map((role) => role.level);
  return levelFilterList.some((l) => currentRoleLevels.includes(l))
    ? FilterResult.MATCH
    : FilterResult.NO_MATCH;
};

export const matchesEveryAnswerFilter = (
  answerFilterList: PerformanceAssessmentAnswerFilter[],
  assessment?: ComplianceProgramReportAssessmentFragment | null
) => {
  if (answerFilterList.length === 0) {
    return FilterResult.NOT_APPLICABLE;
  }
  if (!assessment) {
    return FilterResult.NO_MATCH;
  }
  const answers = assessment.answers;
  return answerFilterList.every(
    (answerFilter) =>
      !!answers.find(
        (a) =>
          isAssessmentReportAnswerType(a) &&
          a.integerAnswer === answerFilter.integerAnswer &&
          a.questionId === answerFilter.questionId
      )
  )
    ? FilterResult.MATCH
    : FilterResult.NO_MATCH;
};

const matchesSomeAnswerFilter = (
  answerFilterList: PerformanceAssessmentAnswerFilter[],
  assessment?: ComplianceProgramReportAssessmentFragment | null
) => {
  if (answerFilterList.length === 0) {
    return FilterResult.NOT_APPLICABLE;
  }
  if (!assessment) {
    return FilterResult.NO_MATCH;
  }
  const answers = assessment.answers;
  return answerFilterList.some(
    (answerFilter) =>
      !!answers.find(
        (a) =>
          isAssessmentReportAnswerType(a) &&
          a.integerAnswer === answerFilter.integerAnswer &&
          a.questionId === answerFilter.questionId
      )
  )
    ? FilterResult.MATCH
    : FilterResult.NO_MATCH;
};

const evaluateFilterResults = (
  results: FilterResult[],
  matchType: AssessmentReportFilterMatchType
): boolean => {
  const applicableResults = results.filter(
    (result) => result !== FilterResult.NOT_APPLICABLE
  );

  if (applicableResults.length === 0) {
    return true;
  }

  if (matchType === AssessmentReportFilterMatchType.MATCH_ALL) {
    return applicableResults.every((result) => result === FilterResult.MATCH);
  } else {
    return applicableResults.some((result) => result === FilterResult.MATCH);
  }
};

export const matchesUpwardAssessmentFilter = (opts: {
  userFilterList: BasicUser[];
  reportsToFilterList: BasicUser[];
  teamFilterList: TeamPickerTeam[];
  target: ComplianceProgramTargetFragment;
  matchType: AssessmentReportFilterMatchType;
}) => {
  const {
    userFilterList,
    reportsToFilterList,
    teamFilterList,
    target,
    matchType,
  } = opts;

  return evaluateFilterResults(
    [
      matchesUserFilter(userFilterList, target),
      matchesReportsToFilter(reportsToFilterList, target),
      matchesTeamsFilter(teamFilterList, target),
    ],
    matchType
  );
};

export const matchesPerformanceAssessmentFilter = (opts: {
  userFilterList: BasicUser[];
  reportsToFilterList: BasicUser[];
  teamFilterList: TeamPickerTeam[];
  enrollmentDateFilterList: (string | null)[];
  roleFilterList: CareerTrackRolePickerRole[];
  careerTrackFilterList: CareerTrackPickerTrack[];
  careerTrackLevelFilterList: number[];
  talentIndicatorFilterList: PerformanceAssessmentAnswerFilter[][];
  reportRow: ComplianceProgramPerformanceReportRowFragment;
  matchType: AssessmentReportFilterMatchType;
}) => {
  const {
    userFilterList,
    reportsToFilterList,
    teamFilterList,
    enrollmentDateFilterList,
    roleFilterList,
    careerTrackFilterList,
    careerTrackLevelFilterList,
    talentIndicatorFilterList,
    reportRow,
    matchType,
  } = opts;
  const assessment = reportRow.assessment;

  const talentIndicatorResult = talentIndicatorFilterList.map((filter) =>
    matchesSomeAnswerFilter(filter, assessment)
  );

  return evaluateFilterResults(
    [
      matchesUserFilter(userFilterList, reportRow.user),
      matchesReportsToFilter(reportsToFilterList, reportRow.user),
      matchesTeamsFilter(teamFilterList, reportRow.user),
      matchesEnrollmentDateFilter(
        enrollmentDateFilterList,
        reportRow.targetAddedToProgramOn
      ),
      matchesRoleFilter(roleFilterList, reportRow.roles),
      matchesCareerTrackFilter(careerTrackFilterList, reportRow.roles),
      matchesCareerTrackLevelFilter(
        careerTrackLevelFilterList,
        reportRow.roles
      ),
      ...talentIndicatorResult,
    ],
    matchType
  );
};
